import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, img } from "../lib/api";
import { useSite } from "../lib/store";
import { ProductCard, Empty } from "../components/Bits";
import NewsletterBox from "../components/NewsletterBox";

import { useSeo, crumbs } from "../components/Seo";

export default function Shop() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const { site } = useSite();
  const [items, setItems] = useState(null);
  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "default";
  const onlyNew = sp.get("novinky") === "1";
  const cat = site?.categories.find((c) => c.slug === slug);
  useSeo({
    title: q ? `Hledání: ${q}` : onlyNew ? "Novinky" : cat ? cat.seo_title || `${cat.name} – materiály pro MŠ ke stažení` : "E-shop – všechny materiály pro školky",
    description: cat ? cat.description || `${cat.name}: pracovní listy, přípravy a aktivity pro mateřské školy i rodiče v PDF ke stažení ihned po zaplacení.` : "Všechny materiály pro mateřské školy na jednom místě: týdenní přípravy, pracovní listy, stezky, tvoření a materiály zdarma.",
    image: cat?.image,
    noindex: !!q,
    jsonld: (s) => [cat ? crumbs(s, [["E-shop", "/eshop"], [cat.name, `/kategorie/${cat.slug}`]]) : crumbs(s, [["E-shop", "/eshop"]])],
  });
  useEffect(() => {
    setItems(null);
    const params = new URLSearchParams({ sort });
    if (slug) params.set("category", slug);
    if (q) params.set("q", q);
    if (onlyNew) params.set("is_new", "true");
    api.get(`/products?${params}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [slug, q, sort, onlyNew]);
  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    v ? n.set(k, v) : n.delete(k);
    setSp(n);
  };
  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="shop-page">
      <div className="flex flex-col md:flex-row md:items-center gap-6 mb-10">
        {cat?.image && <img src={img(cat.image)} alt="" className="w-24 h-24 rounded-full object-cover border-4 border-[var(--pink-soft)]" />}
        <div>
          <p className="eyebrow mb-1">{slug ? "Kategorie" : "E-shop"}</p>
          <h1 className="text-4xl sm:text-5xl font-semibold" data-testid="shop-title">{cat?.name || (q ? `Hledání: ${q}` : onlyNew ? "Novinky" : "Všechny materiály")}</h1>
          {cat?.description && <p className="text-[var(--muted)] mt-2">{cat.description}</p>}
        </div>
      </div>
      {cat?.is_free && <div className="mb-10"><NewsletterBox compact title="Materiály zdarma za odběr newsletteru" text="Zadejte e-mail a všechny materiály v této kategorii si můžete ihned stáhnout." /></div>}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6" data-testid="category-pills">
        <Link to="/eshop" className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${!slug ? "bg-[var(--pink)] border-[var(--pink)] text-white" : "border-[#f3dbe6] hover:border-[var(--pink)]"}`}>Vše</Link>
        {site?.categories.map((c) => (
          <Link key={c.id} to={`/kategorie/${c.slug}`} data-testid={`category-pill-${c.slug}`} className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${slug === c.slug ? "bg-[var(--pink)] border-[var(--pink)] text-white" : "border-[#f3dbe6] hover:border-[var(--pink)]"}`}>{c.name}</Link>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-between mb-8">
        <div className="relative sm:w-80">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input data-testid="shop-search-input" defaultValue={q} onKeyDown={(e) => e.key === "Enter" && setParam("q", e.target.value)} placeholder="Hledat a stisknout Enter" className="field !pl-10 !rounded-full" />
        </div>
        <select data-testid="shop-sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)} className="field !w-auto !rounded-full">
          <option value="default">Doporučené</option>
          <option value="new">Nejnovější</option>
          <option value="popular">Nejprodávanější</option>
          <option value="rating">Nejlépe hodnocené</option>
          <option value="price_asc">Od nejlevnějších</option>
          <option value="price_desc">Od nejdražších</option>
        </select>
      </div>
      {items === null ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] rounded-[28px] bg-[var(--blush)] animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <Empty>Tady zatím nic není… ale brzy přibude!</Empty>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="product-grid">{items.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}</div>
      )}
    </div>
  );
}
